import { BoxComponent, ButtonComponent, TypographyComponent } from '@zeus/web-components';
import expandIcon from '../../../online/resources/assets/expand_icon.svg';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useCart } from '../../context/CartContext';
import { Styles } from '../(routes)/online-ordering/[resturants]/_components/cart.styles';
import CartItem from './CartItem';
import AddIcon from '@mui/icons-material/Add';
import { getCurrentRestaurant, setUserAttribute } from '../../../online/utils/UtilFunctions';
import { dynamicBusinessRoute } from '../../config/routes';
import { Drawer } from '@mui/material';
import { EventAction, EventType, QREventActions } from '../../config/GAConstants';
import { sendGAEvent } from '@next/third-parties/google';
import { getCookie } from 'cookies-next';
import { startTransition } from 'react';
import useAuthUser from '../hooks/auth/AuthUser';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { updateUserAuth } from '@store/online/auth/authSlice';

type ViewCartModalProps = {
  isCartModalOpen: boolean;
  setIsCartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ViewCartModal = ({ isCartModalOpen, setIsCartModalOpen }: ViewCartModalProps) => {
  const { state, actions } = useCart();
  const router = useRouter();
  const pathname = usePathname();
  const authUser = useAuthUser() || {};
  const dispatch = useDispatch();
  //store states
  const isQr = useSelector((state: RootState) => state.restaurant.isQr);
  const selectedBusinessId = useSelector((state: RootState) => state.restaurant.selectedBusinessId);
  const tenantName = useSelector((state: RootState) => state.restaurant.tenantName);
  const orderTime = useSelector((state: RootState) => state.cart.orderingTime);
  const guestSessionId = useSelector((state: RootState) => state.auth.guestSessionId);

  const handleCartClose = (event: React.MouseEvent) => {
    const clickOutside = (event.target as HTMLElement).classList.contains('MuiModal-backdrop');
    if (clickOutside) {
      setIsCartModalOpen(false);
    }
  };
  const saveCart = () => {
    const restaurant = getCurrentRestaurant();
    const customerUsername = authUser.username || undefined;
    if (guestSessionId && selectedBusinessId) {
      const currentLocalCart = (orderTime || {})[selectedBusinessId]?.cart;
      actions.saveCart(
        { ...currentLocalCart, restaurantId: restaurant.id, guestSessionId, customerUsername },
        async (newState) => {
          const newPath = isQr ? `/online-ordering/checkout?qr=true` : '/online-ordering/checkout';
          await new Promise<void>((resolveNav) => {
            startTransition(() => {
              router.push(newPath);
            });
            resolveNav();
          });
          await new Promise<void>((resolve) => {
            actions.syncCart({ ...{ newState, showOutStockError: false }, cartType: 'remote' }, () => resolve());
          });
        }
      );
    }
  };

  const handleIconClick = () => {
    const currentPathSegments = pathname.split('/');

    if (currentPathSegments.includes('cart')) {
      setIsCartModalOpen(false);
    } else {
      router.push(`/online-ordering/${tenantName}/cart`);
    }
  };

  const checkUserStatus = () => {
    const token = getCookie('AccessToken') || '';
    if (token) {
      dispatch(updateUserAuth({ isGuest: false }));
    } else {
      dispatch(updateUserAuth({ isGuest: true }));
    }
  };
  const handleButtonClick = async () => {
    checkUserStatus();
    const user = setUserAttribute();
    if (state && state?.cartOptions.isCartUpdated) {
      await actions.saveCart();
      await actions.syncCart({ ...state?.cart, showOutStockError: false });
      const eventAction = isQr ? QREventActions.QR_CART_CHECKOUT : EventAction.OL_CART_CHECKOUT;
      const eventProperties = {
        total_order_value: state?.cart?.paymentInfo?.discountedPrice,
        number_cart_items:
          state?.cart?.orderedQuantity && state?.cart?.orderedQuantity > 0 ? state?.cart?.orderedQuantity : null,
        ...user,
      };
      sendGAEvent(EventType.EVENT, eventAction, eventProperties);
    }
    await saveCart();
    if (user?.ol_userPhoneNo === null) {
      sendGAEvent(EventType.EVENT, EventAction.OL_CART_CHECKOUT_AS_GUEST);
    }
  };
  const handleAddmore = async () => {
    const currentPathSegments = pathname.split('/').filter(Boolean); // Remove empty strings
    const lastPathSegment = currentPathSegments[currentPathSegments.length - 1];

    const activeRestaurant = getCookie('activeRestaurant')
      ? (JSON.parse(getCookie('activeRestaurant')!) as { businessAlias: string })
      : null;

    const activeAlias = activeRestaurant?.businessAlias;

    if (activeAlias && currentPathSegments.includes(activeAlias) && lastPathSegment === activeAlias) {
      setIsCartModalOpen(false);
    } else {
      if (state?.cartOptions?.isCartUpdated) {
        await actions.saveCart();
        await actions.syncCart(state.cart);
      }
      router.push(dynamicBusinessRoute());
    }
  };

  if (state && state?.cart?.items) {
    const { items: cartItems } = state.cart ?? {};

    const qty = state?.cart?.orderedQuantity ? state?.cart?.orderedQuantity : 0;

    return (
      <BoxComponent onClick={handleCartClose} sx={{ cursor: 'pointer' }} id="cart-modal-box">
        <Drawer
          anchor="top"
          open={isCartModalOpen}
          onClose={handleCartClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: '700px', // Fixed width for cart container
              marginLeft: 'auto', // Aligns the cart to the right
              marginTop: '20px', // Slight top margin for spacing
              borderBottomLeftRadius: '8px', // Rounded bottom-left corner
              boxShadow: ' #C0C0CA',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: '12px',
              position: 'absolute',
              top: '5rem',
              backgroundColor: 'rgb(236,236,245)',
            },
          }}
        >
          <>
            <BoxComponent sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <TypographyComponent sx={{ fontSize: '24px', fontWeight: 500, paddingLeft: '10px' }}>
                {state?.cart.orderedQuantity === 1 ? <>Item </> : <>Items </>}
                {`(${qty})`}{' '}
              </TypographyComponent>

              <Image
                src={expandIcon}
                alt="expandIcon"
                style={{ cursor: 'pointer' }}
                id="cart-expand-icon"
                onClick={handleIconClick}
              />
            </BoxComponent>

            {cartItems?.length > 0 ? (
              <BoxComponent
                sx={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                }}
                id="cart-items-container"
              >
                {cartItems.map((item, index) => (
                  <div style={Styles.cartItemContainer} key={index} id={`cart-item-${index}`}>
                    <CartItem item={item} itemIndex={index} />
                  </div>
                ))}
              </BoxComponent>
            ) : (
              <BoxComponent sx={Styles.emptyCartContainer} id="empty-cart-container">
                <Image src="/assets/EmptyCart.svg" width={150} height={100} alt="empty_cart" />
                <TypographyComponent sx={Styles.emptyCartText}>Your cart is empty</TypographyComponent>
                <BoxComponent sx={Styles.addButton} onClick={handleAddmore} id="add-items-button">
                  <AddIcon sx={Styles.addIcon} />
                  <TypographyComponent sx={Styles.additemButtonText}>Add Items</TypographyComponent>
                </BoxComponent>
              </BoxComponent>
            )}

            {cartItems?.length > 0 && (
              <BoxComponent sx={{ display: 'flex', justifyContent: 'center', paddingTop: '30px' }}>
                <ButtonComponent
                  id="cart-checkout-button"
                  variant="contained"
                  size="large"
                  style={{ width: '200px', height: '40px' }}
                  onClick={handleButtonClick}
                >
                  Checkout ${state?.cart?.discountedPrice ? Number(state?.cart?.discountedPrice).toFixed(2) : 0}
                </ButtonComponent>
              </BoxComponent>
            )}
          </>
        </Drawer>
      </BoxComponent>
    );
  }
};
